import "./pages.css";
import React, { useState, useEffect, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { iconos_arrow } from './iconos_imagenes';
import { Mail } from 'lucide-react';

export function BrandItem({ imgSrc, altText, linkHref, linkText, description }) {
  return (
    <div className="brandCard">
      <div className="brandLogo">
        <img src={imgSrc} alt={altText} />
      </div>
      <h3 className="brandName">{linkText}</h3>
      <p className="brandDescription">{description}</p>
      <div className="brandActions">
        <img 
          src={require('../images/iconosauxiliares/espana.png')} 
          alt="Fabricado en España" 
          className="actionIcon" 
        />
        <img 
          src={require('../images/iconosauxiliares/hoja.png')} 
          alt="Eco-friendly" 
          className="actionIcon" 
        />
        <a href={linkHref} target="_blank" rel="noopener noreferrer" className="websiteLink">
          Sitio web
        </a>
      </div>
    </div>
  );
}

// Helper function to get unique stores from all subcategories
function getUniqueStores(types) {
  if (!types) return [];
  
  // Get all stores from all subcategories
  const allStores = types.reduce((acc, type) => {
    if (type.stores) {
      return [...acc, ...type.stores];
    }
    return acc;
  }, []);

  // Create a Map using link_text as the key to remove duplicates
  const storeMap = new Map();
  allStores.forEach(store => {
    if (!storeMap.has(store.link_text)) {
      storeMap.set(store.link_text, store);
    }
  });

  // Convert Map back to array
  return Array.from(storeMap.values());
}

export function GetSubcatShops({ category_name, searchTerm, activeTab }) {
  if (!category_name?.types) return <div className="loading">Cargando...</div>;

  // If there's a search term, search through all stores
  if (searchTerm) {
    const uniqueStores = getUniqueStores(category_name.types);
    const filteredStores = uniqueStores.filter(store => 
      store.link_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
      store.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (filteredStores.length === 0) {
      return <div className="noData">No se encontraron marcas que coincidan con la búsqueda</div>;
    }

    return (
      <div className="brandsContainer">
        {filteredStores.map(store => (
          <BrandItem
            key={store.link_text}
            imgSrc={require(`../images/iconostiendas${store.image_path}`)}
            altText={store.alternative_text}
            linkHref={store.store_url}
            linkText={store.link_text}
            description={store.description}
          />
        ))}
      </div>
    );
  }

  // If "Mostrar Todo" is selected (activeTab === 0)
  if (activeTab === 0) {
    const uniqueStores = getUniqueStores(category_name.types);
    return (
      <div className="brandsContainer">
        {uniqueStores.map(store => (
          <BrandItem
            key={store.link_text}
            imgSrc={require(`../images/iconostiendas${store.image_path}`)}
            altText={store.alternative_text}
            linkHref={store.store_url}
            linkText={store.link_text}
            description={store.description}
          />
        ))}
      </div>
    );
  }

  // For other tabs, show stores of the selected subcategory (subtract 1 from activeTab because of "Mostrar Todo")
  const stores = category_name.types[activeTab - 1]?.stores;
  if (!stores) return <div className="loading">Cargando...</div>;
  
  return (
    <div className="brandsContainer">
      {stores.map(store => (
        <BrandItem
          key={store.link_text}
          imgSrc={require(`../images/iconostiendas${store.image_path}`)}
          altText={store.alternative_text}
          linkHref={store.store_url}
          linkText={store.link_text}
          description={store.description}
        />
      ))}
    </div>
  );
}

export default function RopaMain({ BackendData }) {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const dataRopaCategory = useMemo(() => BackendData || {}, [BackendData]);
  const TitleName = dataRopaCategory.title;
  const [subtitles, setSubtitles] = useState([]);

  useEffect(() => {
    if (dataRopaCategory.types) {
      // Add "Todo" as the first option
      const dynamicSubtitles = [
        { name: "Todo", image_path: "" },
        ...dataRopaCategory.types.map((type) => ({
          name: type.name,
          image_path: type.image_path,
        }))
      ];
      setSubtitles(dynamicSubtitles);
    }
  }, [dataRopaCategory]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <div className="categoryPage">
      <h1 className="pageTitle">{TitleName}</h1>
      
      <div className="searchBar">
        <input 
          type="text" 
          placeholder="Buscar marca..." 
          className="searchInput"
          value={searchTerm}
          onChange={handleSearch}
        />
      </div>

      <nav className="categoryNav">
        {subtitles.map((subtitle, index) => (
          <button
            key={index}
            className={`navButton ${activeTab === index ? 'active' : ''} ${index === 0 ? 'todo' : ''}`}
            onClick={() => {
              setActiveTab(index);
              setSearchTerm(''); // Clear search when changing tabs
            }}
          >
            {subtitle.name}
          </button>
        ))}
      </nav>

      <section className="categoryContent">
        <h2 className="sectionTitle">
          {searchTerm ? 'Resultados de búsqueda' : subtitles[activeTab]?.name}
        </h2>
        <GetSubcatShops
          category_name={dataRopaCategory}
          searchTerm={searchTerm}
          activeTab={activeTab}
        />
      </section>

      <button
        className="scrollToTop"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Scroll to Top"
      >
        <img src={iconos_arrow.iconArrow} alt="Scroll to Top" />
      </button>

      <footer className="footer">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div style={{ width: '30px' }}></div> {/* Empty div for spacing */}
          <p style={{ textAlign: 'center', margin: '0 auto' }}>
            Powered by{' '}
            <a 
              href="https://github.com/tu-usuario/tu-repositorio" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              © 2025 
            </a>
          </p>
          <a 
            href="mailto:marcas_ropa_y_calzado@hotmail.com" 
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Mail size={24} color="currentColor" />
          </a>
        </div>
      </footer>
    </div>
  );
}